import type React from "react";
import { useState, useEffect } from "react";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	Grid,
	Snackbar,
	Typography,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { ErrorModal } from "@dexteel/mesf-core";
import { AssetPartsTable } from "./asset-parts-table";
import { useTemplatesContext } from "@/pages/templates/context/templatesContext";
import {
	linkTemplateParts,
	unLinkTemplateParts,
} from "@/pages/templates/repositories/TemplatesRepositorie";
import type { TemplatePart } from "../../../../models/TemplatePart";

interface Props {
	show: boolean;
	onHide: (shouldUpdate: boolean) => void;
	TemplateId: number | null;
	TemplateName: string;
	Title: string;
	AssetName: string;
}

export const AssetPartsPicker: React.FC<Props> = ({
	show,
	onHide,
	TemplateId,
	TemplateName,
	Title,
	AssetName,
}) => {
	const {
		state: { templatePartsList },
	} = useTemplatesContext();

	const [selectedParts, setSelectedParts] = useState<TemplatePart[]>([]);
	const [error, setError] = useState("");
	const [successMessage, setSuccessMessage] = useState("");

	useEffect(() => {
		if (show) {
			setSelectedParts(
				templatePartsList.filter((part) => part.IsAssetResource),
			);
		}
	}, [show, templatePartsList]);

	const handleSelectionChanged = (newSelectedParts: TemplatePart[]) => {
		setSelectedParts(newSelectedParts);
	};

	const getNewlyAddedParts = (
		currentSelected: TemplatePart[],
		originalList: TemplatePart[],
	): TemplatePart[] => {
		return currentSelected.filter(
			(selectedPart) =>
				!originalList.some(
					(existingPart) =>
						existingPart.PartName === selectedPart.PartName &&
						existingPart.IsAssetResource,
				),
		);
	};

	const getRemovedParts = (
		currentSelected: TemplatePart[],
		originalList: TemplatePart[],
	): TemplatePart[] => {
		return originalList.filter(
			(originalPart) =>
				originalPart.IsAssetResource &&
				!currentSelected.some(
					(selectedPart) => selectedPart.PartName === originalPart.PartName,
				),
		);
	};

	const handleSave = async () => {
		const newlyAddedParts = getNewlyAddedParts(
			selectedParts,
			templatePartsList,
		);
		const removedParts = getRemovedParts(selectedParts, templatePartsList);

		if (newlyAddedParts.length > 0) {
			const newAssetParts = newlyAddedParts.map((part) => ({
				PartName: part.PartName,
				IsAssetResource: true,
			}));
			await linkTemplateParts(TemplateId as number, newAssetParts as any);
			onHide(true);
		} else onHide(false);

		if (removedParts.length > 0) {
			const assetPartsToRemoved = removedParts.map((part) => ({
				PartName: part.PartName,
				IsAssetResource: true,
			}));
			await unLinkTemplateParts(
				TemplateId as number,
				assetPartsToRemoved as any,
			);
			onHide(true);
		} else onHide(false);
	};

	return (
		<>
			<Dialog open={show} onClose={() => onHide(false)} maxWidth="md" fullWidth>
				<DialogTitle style={{ padding: "24px 24px 0" }}>
					<Typography variant="h5" style={{ margin: "0", fontWeight: 600 }}>
						PARTS FROM ASSET
					</Typography>
					<Typography
						style={{
							display: "flex",
							alignItems: "center",
							fontSize: "1.3rem",
							flexWrap: "wrap",
							gap: "8px",
							padding: "8px 0 0",
						}}
					>
						<div
							style={{
								display: "inline-flex",
								alignItems: "center",
								whiteSpace: "nowrap",
								margin: "4px 0",
							}}
						>
							<span style={{ fontWeight: 600 }}>Template:</span>
							<span style={{ marginLeft: "4px" }}>{TemplateName}</span>
						</div>
						<div
							style={{
								display: "inline-flex",
								alignItems: "center",
								whiteSpace: "nowrap",
								margin: "4px 8px",
							}}
						>
							|
						</div>
						<div
							style={{
								display: "inline-flex",
								alignItems: "center",
								whiteSpace: "nowrap",
								margin: "4px 0",
							}}
						>
							<span style={{ fontWeight: 600 }}>Title:</span>
							<span style={{ marginLeft: "4px" }}>{Title}</span>
						</div>
						<div
							style={{
								display: "inline-flex",
								alignItems: "center",
								whiteSpace: "nowrap",
								margin: "4px 8px",
							}}
						>
							|
						</div>
						<div
							style={{
								display: "inline-flex",
								alignItems: "center",
								whiteSpace: "nowrap",
								margin: "4px 0",
							}}
						>
							<span style={{ fontWeight: 600 }}>Asset:</span>
							<span style={{ marginLeft: "4px" }}>{AssetName}</span>
						</div>
					</Typography>
				</DialogTitle>
				<DialogContent>
					<AssetPartsTable
						templateParts={templatePartsList.filter(
							(part) => part.IsAssetResource,
						)}
						onSelectionChanged={handleSelectionChanged}
					/>
				</DialogContent>
				<DialogActions style={{ padding: "16px 24px 24px" }}>
					<Grid container spacing={2} justifyContent="flex-end">
						<Grid item>
							<Button
								variant="contained"
								color="default"
								onClick={() => onHide(false)}
							>
								Cancel
							</Button>
						</Grid>
						<Grid item>
							<Button variant="contained" color="primary" onClick={handleSave}>
								Save
							</Button>
						</Grid>
					</Grid>
				</DialogActions>
			</Dialog>
			<ErrorModal error={error} onHide={() => setError("")} />
			<Snackbar
				open={!!successMessage}
				autoHideDuration={6000}
				onClose={() => setSuccessMessage("")}
			>
				<Alert onClose={() => setSuccessMessage("")} severity="success">
					{successMessage}
				</Alert>
			</Snackbar>
		</>
	);
};
